/**
 * PATH: apps/qa/src/pages/portal/api.ts
 * PURPOSE: API functions for the external QA portal
 */

import { API_BASE } from '@quotech/api'
import { PortalRequestError, type PortalData, type PortalSignPayload } from './types'

const portalUrl = (token: string, action = '') => `${API_BASE}/api/qa/portal/${token}/${action}`

export async function fetchPortalData(token: string): Promise<PortalData> {
  const response = await fetch(portalUrl(token))
  if (!response.ok) {
    if (response.status === 410) throw new PortalRequestError('expired', 'This report link has expired.')
    if (response.status === 404) throw new PortalRequestError('invalid', 'This report link is invalid or has been revoked.')
    if (response.status === 403) throw new PortalRequestError('revoked', 'Access to this report has been revoked.')
    throw new PortalRequestError('unavailable', 'The report portal is temporarily unavailable.')
  }
  return response.json()
}

export function getPortalDownloadUrl(token: string) {
  return portalUrl(token, 'download/')
}

export async function submitPortalComment(token: string, comment: string): Promise<{ id: string; created_at: string }> {
  return postPortal(token, 'comments/', { comment })
}

export async function submitPortalSignature(token: string, payload: PortalSignPayload): Promise<{ id: string; decision: 'signed' }> {
  return postPortal(token, 'sign/', payload)
}

export interface PortalSignatureUpload {
  file_id: string
  filename: string
  content_type: string
}

export async function uploadPortalSignatureFile(token: string, file: File): Promise<PortalSignatureUpload> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(portalUrl(token, 'signature-file/'), {
    method: 'POST',
    body: form,
  })
  if (!response.ok) throw new Error(await actionError(response, 'The signature file could not be uploaded.'))
  const data = await response.json() as PortalSignatureUpload
  if (!data.file_id) throw new Error('The upload completed without a FileRecord identifier.')
  return data
}

async function postPortal<T>(token: string, action: string, payload: object): Promise<T> {
  const response = await fetch(portalUrl(token, action), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw new Error(await actionError(response, 'The portal action could not be completed.'))
  }
  return response.json()
}

async function actionError(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({})) as { error?: string; detail?: string }
  if (response.status === 404) return body.error || 'This report link is invalid or has been revoked.'
  if (response.status === 410) return body.error || 'This report link has expired.'
  if (response.status === 403) return body.error || 'Permission denied for this report action.'
  if (response.status === 400) return body.error || body.detail || 'The signature file is invalid.'
  if (response.status === 413) return body.error || 'The signature file is too large.'
  if (response.status === 409) return body.error || 'This report revision changed. Reload before continuing.'
  if (response.status === 422) return body.detail || body.error || 'The submitted report action is invalid.'
  return body.error || body.detail || fallback
}
